import React, { useMemo } from "react";
import classNames from "classnames";
import moment from "moment";

import {
  formatDuration,
  PERMISSION,
  asSeconds,
} from "@highnote/server/src/core/shared-util";
import { ReactComponent as SpaceOwnerStarSVG } from "App/common/icons-v2/star-fill.svg";
import { ReactComponent as CheckSVG } from "App/common/icons/check.svg";
import { useAuth } from "App/components/Auth";
import { useCarouselSync } from "App/routes/Main/Space/Carousel/useCarouselSync";
import { Button, BUTTON_THEME } from "../../core/Button";
import { StatusIndicator } from "../StatusIndicator";
import { getValidCommentBlock, useCommentContext } from "../useComment";
import {
  ConnectedUserAvatar,
  ConnectedUserName,
  USER_AVATAR_SIZE,
} from "../UserAvatar/UserAvatar";
import { useSpaceContext } from "../useSpace";
import { COMMENT_BLOCKS } from "./blocks";
import "./CommentMeta.scss";
import { CommentActions, DEFAULT_COMMENT_ACTION } from "./CommentActions";
import { useUrlContext } from "App/routes/Main/useUrlContext";

enum COMMENT_META_VARIANT {
  DEFAULT = "default",
  INLINE = "inline",
}

export const CommentMeta = ({
  variant,
  hideCreatedAtTimestamp,
  hideUsername,
  type = "default",
  showActions,
  localInstanceId,
}: {
  variant?: COMMENT_META_VARIANT;
  type?: "default" | "space-chat";
  hideCreatedAtTimestamp?: boolean;
  hideUsername?: boolean;
  showActions?: boolean;
  localInstanceId?: string;
}) => {
  const { isAllowed, user } = useAuth();
  const { spaceId, trackId } = useUrlContext();
  const isTrackPage = !!(trackId && spaceId);
  const { space } = useSpaceContext();
  const { isPublished, comment, saveComment } = useCommentContext();
  const isCurrentUser = comment?.createdBy === user?.id;
  const isSpaceOwner = comment?.createdBy === space?.createdBy;
  const sync = useCarouselSync();
  const blockData = getValidCommentBlock(comment);

  const canResolve = isAllowed(PERMISSION.TO_RESOLVE_COMMENT_IN_SPACE, {
    space,
  });
  const isResolvable =
    !comment?.parentComment && !!comment?.trackId && canResolve;
  const isHighnoteAdmin = !!user?.cohorts?.admins;
  const isTopLevelComment = !comment?.parentComment;
  const allActions = useMemo(
    () => [
      ...(isResolvable ? [DEFAULT_COMMENT_ACTION.TOGGLE_RESOLVE] : []),
      DEFAULT_COMMENT_ACTION.EDIT,
      DEFAULT_COMMENT_ACTION.COPY,
      DEFAULT_COMMENT_ACTION.DELETE,
      // Only show delete replies for top-level comments
      ...(isHighnoteAdmin && isTopLevelComment
        ? [DEFAULT_COMMENT_ACTION.DELETE_REPLIES]
        : []),
      // Admin delete works on all comments
      ...(isHighnoteAdmin ? [DEFAULT_COMMENT_ACTION.ADMIN_DELETE_COMMENT] : []),
    ],
    [isResolvable, isHighnoteAdmin, isTopLevelComment],
  );

  if (!blockData) return null;

  const blockType = blockData.type as keyof typeof COMMENT_BLOCKS;
  const isStickerType = ["custom", "sticker"].includes(blockType);
  const blockConfig = COMMENT_BLOCKS[isStickerType ? "sticker" : blockType];

  if (!blockConfig) {
    console.log(
      "[CommentMeta] Could not find matching block config:",
      blockType,
    );
    return null;
  }

  if (!blockConfig.Component || !blockConfig.Icon) {
    return null;
  }

  return (
    <div
      className={classNames("highnote-comment-meta", {
        "is-current-user": type === "space-chat" && isCurrentUser,
        "highnote-comment-meta-space-chat": type === "space-chat",
      })}
      data-variant={variant}
    >
      {variant !== COMMENT_META_VARIANT.INLINE && (
        <>
          <ConnectedUserAvatar
            userId={comment.createdBy}
            size={USER_AVATAR_SIZE.MEDIUM}
          />
          <div className="comment-meta-content">
            <div className="user-info-row">
              <div className="user-name">
                {!hideUsername && (
                  <ConnectedUserName userId={comment.createdBy} />
                )}
                {isSpaceOwner && (
                  <div className="highnote-comment-meta-space-owner-icon">
                    <SpaceOwnerStarSVG />
                  </div>
                )}
              </div>
              <div className="tools">
                {comment.timestamp !== undefined &&
                  comment.trackId &&
                  isTrackPage && (
                    <>
                      {comment.isResolved ? <CheckSVG /> : <blockConfig.Icon />}
                      <Button
                        theme={BUTTON_THEME.PLAIN}
                        className="timestamp"
                        onClick={() => {
                          sync.onCardIdClick(comment.id);
                        }}
                      >
                        {formatDuration(asSeconds(comment.timestamp))}
                      </Button>
                    </>
                  )}

                <StatusIndicator
                  isLoading={comment.isSaving}
                  errorMessage={comment.updateError}
                  retryCallback={!isPublished ? saveComment : undefined}
                  className="space-chat-comment-status-indicator"
                />

                {showActions && isPublished && !comment.isSaving && (
                  <CommentActions
                    actions={allActions}
                    localInstanceId={localInstanceId}
                  />
                )}
              </div>
            </div>
            {!hideCreatedAtTimestamp && (
              <div className="created-at">
                {moment(comment.createdAt).fromNow()}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
